import { CheckIcon, CopyIcon, ExternalLinkIcon, MessageSquareTextIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useJobs } from '@/features/jobs/queries';
import { useMe } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { fmtDateTime, fmtNumber } from '@/lib/format';
import { resolveTenantSlug, tenantSlugFromHost } from '@/lib/tenant';
import type { TenantSettingsPatch, TenantSettingsView } from '@/lib/types';
import { useWebConsentStats } from './queries';

type SaveFn = (patch: TenantSettingsPatch, okMessage?: string) => Promise<void>;

function Section({ title, description, children, footer }: { title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className="bg-card border">
      <div className="border-b px-5 py-4">
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
      </div>
      <div className="grid gap-4 px-5 py-4">{children}</div>
      {footer && <div className="flex items-center justify-end gap-2 border-t px-5 py-3">{footer}</div>}
    </section>
  );
}

/**
 * The public URL of the sign-up page: a static page set in the operations console when there is one (root-relative
 * or absolute), else the organization's sign-in domain when this browser is on one, else the slug form.
 */
export function consentPageUrl(path: '/opt-in' | '/opt-out' = '/opt-in', override?: string | null): string {
  const origin = window.location.origin;
  const custom = override?.trim();
  if (custom) return /^https?:/i.test(custom) ? custom : `${origin}${custom.startsWith('/') ? '' : '/'}${custom}`;
  return tenantSlugFromHost() ? `${origin}${path}` : `${origin}${path}/${encodeURIComponent(resolveTenantSlug())}`;
}

/** The paragraph a 10DLC campaign registration asks for under "How do end-users consent?", filled in for this organization. */
export function campaignConsentText(org: string, program: string, url: string, optOutUrl: string): string {
  return (
    `People enroll on the ${org} job-site enrollment web page at ${url}. Enrollment asks for the job site and the person's name. ` +
    `Text messaging is a separate, optional choice: the page shows an optional mobile number field and, directly below it, two unchecked checkboxes, "Yes, text me ${program} updates" and "No, do not text me". ` +
    'The form submits with both boxes blank or with "No" ticked, which enrolls the person with no SMS consent and no texts, so the service can be used without agreeing to messages. ' +
    `Someone who wants texts enters their mobile number and ticks "Yes"; the disclosure printed beside the "Yes" box states that by checking the box and submitting their mobile number they agree to receive recurring text messages from ${org} about ${program}, ` +
    'that message frequency varies, that message and data rates may apply, that they can reply STOP to opt out or HELP for help, and that consent is voluntary and not a condition of employment, purchase or service. ' +
    'The Privacy Policy and Terms are linked beside the checkbox and in the page footer. Each opt-in is stored with the exact disclosure text, the date and time, the IP address and the browser, and a confirmation text is sent to the number. ' +
    `Messages are sent only to numbers that gave this affirmative consent; recipients can opt out by replying STOP or at ${optOutUrl}.`
  );
}

function CopyField({ label, value, rows }: { label: string; value: string; rows?: number }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy. Select the text and copy it yourself.');
    }
  };
  return (
    <Field label={label}>
      <div className="flex items-start gap-2">
        {rows ? <Textarea readOnly value={value} rows={rows} className="font-mono text-xs" /> : <Input readOnly value={value} className="font-mono text-xs" />}
        <Button type="button" variant="outline" size="icon" onClick={() => void copy()} aria-label={`Copy ${label.toLowerCase()}`}>
          {copied ? <CheckIcon /> : <CopyIcon />}
        </Button>
      </div>
    </Field>
  );
}

/**
 * Organization → Settings → Text sign-up: the public opt-in / opt-out page. Turn it on, name the program, choose
 * which job sites people can join, and copy the URL and the consent paragraph a carrier registration asks for.
 */
export function ConsentTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const current = view.settings.smsConsent;
  const me = useMe();
  const jobs = useJobs();
  const stats = useWebConsentStats();
  const [enabled, setEnabled] = useState(current.enabled);
  const [programName, setProgramName] = useState(current.programName);
  const [description, setDescription] = useState(current.description);
  const [jobIds, setJobIds] = useState<string[]>(current.jobIds);
  const [sendConfirmation, setSendConfirmation] = useState(current.sendConfirmation);
  useEffect(() => {
    setEnabled(current.enabled);
    setProgramName(current.programName);
    setDescription(current.description);
    setJobIds(current.jobIds);
    setSendConfirmation(current.sendConfirmation);
  }, [current]);

  const activeJobs = (jobs.data?.items ?? []).filter((j) => j.status === 'active');
  const dirty = enabled !== current.enabled || programName !== current.programName || description !== current.description || sendConfirmation !== current.sendConfirmation || jobIds.join(',') !== current.jobIds.join(',');
  const toggleJob = (id: string, on: boolean) => setJobIds((prev) => (on ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id)));
  const { branding } = useBranding();
  const url = consentPageUrl('/opt-in', branding.legal.signupUrl);
  const optOutUrl = consentPageUrl('/opt-out', branding.legal.optOutUrl);
  const program = programName.trim() || 'job-site text alerts';
  const live = current.enabled;

  return (
    <div className="space-y-5">
      <Section
        title="Text sign-up page"
        description="A public page where workers join a job site's roster. Texting is a separate yes-or-no choice on that page; only a ticked “Yes” box asks for a mobile number and stores a consent record with the exact wording, time and network address, which carriers ask for when a messaging campaign is registered."
        footer={
          <Button disabled={!dirty || busy} onClick={() => void onSave({ smsConsent: { enabled, programName: programName.trim(), description: description.trim(), jobIds, sendConfirmation } }, 'Text sign-up settings saved')}>
            Save text sign-up
          </Button>
        }
      >
        <div className="flex items-start justify-between gap-4 border px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MessageSquareTextIcon className="size-4" aria-hidden /> Web sign-up {live ? <span className="text-primary text-xs font-normal">· live</span> : <span className="text-muted-foreground text-xs font-normal">· off</span>}
            </div>
            <p className="text-muted-foreground mt-0.5 text-sm">People can sign up at the page below. Opting out always works, even when sign-up is off.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Web sign-up" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Program name" htmlFor="cs-program" description="Named in the disclosure and the confirmation text, e.g. “the Orlando Tower pilot”.">
            <Input id="cs-program" value={programName} onChange={(e) => setProgramName(e.target.value)} maxLength={80} placeholder="job-site text alerts" />
          </Field>
          <Field label="Confirmation text" description="Send a text confirming the sign-up (recommended; it proves the number).">
            <div className="flex h-9 items-center gap-2">
              <Switch checked={sendConfirmation} onCheckedChange={setSendConfirmation} /> <span className="text-sm">{sendConfirmation ? 'Sent after each sign-up' : 'Not sent'}</span>
            </div>
          </Field>
        </div>
        <Field label="What people should expect" htmlFor="cs-desc" description="Shown at the top of the page. Leave empty for the standard wording.">
          <Textarea id="cs-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500} placeholder={`Sign up for texts about ${program} from your organization: notices about the site, schedule and safety updates, and replies to your questions.`} />
        </Field>
        <Field label="Job sites people can join" description={jobIds.length === 0 ? 'None ticked: every active job site is offered, and new jobs join automatically.' : 'Only the ticked jobs are offered. A single job skips the picker on the page.'}>
          {jobs.isPending ? (
            <p className="text-muted-foreground text-sm">Loading jobs…</p>
          ) : activeJobs.length === 0 ? (
            <p className="text-muted-foreground text-sm">No active jobs yet.</p>
          ) : (
            <ul className="grid gap-1 sm:grid-cols-2">
              {activeJobs.map((j) => {
                const on = jobIds.includes(j.id);
                return (
                  <li key={j.id}>
                    <label className="hover:bg-accent/40 flex cursor-pointer items-center gap-3 border px-3 py-2 text-sm">
                      <Checkbox checked={on} onCheckedChange={(c) => toggleJob(j.id, c === true)} />
                      <span className="min-w-0 flex-1 truncate">{j.name}</span>
                      {!j.phoneNumber && <span className="text-muted-foreground text-xs">no number yet</span>}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </Field>
      </Section>

      <Section title="Share the page" description="Put this address on posters, onboarding paperwork and in the campaign registration. The disclosure wording lives in the operations console, so it stays consistent across organizations unless an operator changes it for you.">
        <CopyField label="Enrollment page" value={url} />
        <CopyField label="Opt-out page" value={optOutUrl} />
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={url} target="_blank" rel="noreferrer">
              <ExternalLinkIcon /> Open the enrollment page
            </a>
          </Button>
        </div>
        <CopyField label="Campaign registration: how do end-users consent?" value={campaignConsentText(me.tenant.name, program, url, optOutUrl)} rows={7} />
      </Section>

      <Section title="Activity" description="Enrollments, text sign-ups and opt-outs made through the page. Text sign-ups are also on the contact's record and in the activity log; an enrollment without texting creates no contact and nothing is sent.">
        <dl className="grid grid-cols-2 gap-px border bg-border sm:grid-cols-3">
          <Stat label="Enrollments, 30 days" value={stats.data ? fmtNumber(stats.data.enrollments30d) : undefined} hint={stats.data ? `${fmtNumber(stats.data.enrollmentsNoSms30d)} without texting` : undefined} />
          <Stat label="Text sign-ups, 30 days" value={stats.data ? fmtNumber(stats.data.optIns30d) : undefined} />
          <Stat label="Opt-outs, 30 days" value={stats.data ? fmtNumber(stats.data.optOuts30d) : undefined} />
          <Stat label="Enrollments, all time" value={stats.data ? fmtNumber(stats.data.enrollmentsTotal) : undefined} />
          <Stat label="Text sign-ups, all time" value={stats.data ? fmtNumber(stats.data.optInsTotal) : undefined} hint={stats.data?.lastOptInAt ? `Last ${fmtDateTime(stats.data.lastOptInAt)}` : 'None yet'} />
          <Stat label="Opt-outs, all time" value={stats.data ? fmtNumber(stats.data.optOutsTotal) : undefined} />
        </dl>
      </Section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string | undefined; hint?: string }) {
  return (
    <div className="bg-card px-4 py-3">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="font-display mt-1 text-2xl leading-none">{value ?? '—'}</dd>
      {hint && <dd className="text-muted-foreground mt-1 text-xs">{hint}</dd>}
    </div>
  );
}
